/**
 * overlayGraph — pure graph helpers for Block 2 control-overlay actions.
 *
 * INIT-2026-Q3-SCENE-HIERARCHY Block 2 (control overlay rendering & actions).
 *
 * These functions compute the ancestor / descendant / sibling sets that the
 * overlay actions (isolate, isolate-system, color-by-aspect) need, then turn
 * a "keep visible" set into a concrete `Record<objectId, ControlOverlayEntry>`
 * delta that the store can merge.
 *
 * Design constraints (OBJECT_IDENTITY_AND_SELECTION_POLICY §7, DESIGN_POLICY §5):
 *   - Pure functions, no side effects, no React, no store access.
 *   - Zero domain branching — operates only on aspect graphs + objectIds.
 *   - `colorOverride` values are TOKEN ALIASES, never raw hex (scene-hierarchy
 *     types §6 — token-only styling rule).
 *
 * Aspect membership follows the SAME strict rule as `treeProjection.ts`
 * (Block 3): a node belongs to aspect A iff `node.aspects[A]` is defined;
 * legacy nodes with no `aspects` object fall back to the top-level `parentId`
 * and are members of both aspects.
 */

import type {
  ControlOverlayEntry,
  UniversalSceneNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { TreeAspect } from "./treeProjection";

// ─────────────────────────────────────────────────────────────────────────────
// Aspect-membership helpers (mirror treeProjection's strict rule)
// ─────────────────────────────────────────────────────────────────────────────

function isLegacyNode(node: UniversalSceneNode): boolean {
  return (node as { aspects?: unknown }).aspects == null;
}

/** True iff `node` is a member of `aspect` (Block 3 strict rule). */
export function isMemberOfAspect(
  node: UniversalSceneNode,
  aspect: TreeAspect,
): boolean {
  if (isLegacyNode(node)) return true;
  return node.aspects[aspect] != null;
}

/**
 * Effective parentId of `node` under `aspect`. Returns `null` for roots and
 * for non-members (callers should gate on `isMemberOfAspect` first).
 */
export function getAspectParentId(
  node: UniversalSceneNode,
  aspect: TreeAspect,
): string | null {
  if (isLegacyNode(node)) return node.parentId;
  return node.aspects[aspect]?.parentId ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Index helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Builds an objectId → node lookup map. */
export function indexNodesById(
  nodes: readonly UniversalSceneNode[],
): Map<string, UniversalSceneNode> {
  const map = new Map<string, UniversalSceneNode>();
  for (const node of nodes) map.set(node.objectId, node);
  return map;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ancestor / descendant traversal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the ancestor objectIds of `objectId` in the given aspect, ordered
 * from the immediate parent up to the root. The starting node is NOT included.
 * Cycle-safe (a visited guard prevents infinite loops on malformed graphs).
 */
export function collectAncestors(
  objectId: string,
  nodeById: ReadonlyMap<string, UniversalSceneNode>,
  aspect: TreeAspect = "spatial",
): string[] {
  const out: string[] = [];
  const seen = new Set<string>([objectId]);
  let current = nodeById.get(objectId);
  while (current) {
    if (!isMemberOfAspect(current, aspect)) break;
    const parentId = getAspectParentId(current, aspect);
    if (parentId == null || seen.has(parentId)) break;
    seen.add(parentId);
    out.push(parentId);
    current = nodeById.get(parentId);
  }
  return out;
}

/**
 * Returns the descendant objectIds of `objectId` in the given aspect (all
 * nodes whose aspect-parent chain reaches `objectId`). The starting node is
 * NOT included. Order is breadth-first. Cycle-safe.
 */
export function collectDescendants(
  objectId: string,
  nodes: readonly UniversalSceneNode[],
  aspect: TreeAspect = "spatial",
): string[] {
  // children map: parentId → child objectIds (members only)
  const childrenByParent = new Map<string, string[]>();
  for (const node of nodes) {
    if (!isMemberOfAspect(node, aspect)) continue;
    const parentId = getAspectParentId(node, aspect);
    if (parentId == null) continue;
    const bucket = childrenByParent.get(parentId);
    if (bucket) bucket.push(node.objectId);
    else childrenByParent.set(parentId, [node.objectId]);
  }

  const out: string[] = [];
  const seen = new Set<string>([objectId]);
  const queue: string[] = [...(childrenByParent.get(objectId) ?? [])];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    const children = childrenByParent.get(id);
    if (children) queue.push(...children);
  }
  return out;
}

/**
 * Returns the top-level group (root ancestor) objectId of `objectId` in the
 * given aspect. For a root node, returns the node's own objectId. For a
 * non-member, returns the node's own objectId (it is its own group).
 */
export function topLevelGroupId(
  objectId: string,
  nodeById: ReadonlyMap<string, UniversalSceneNode>,
  aspect: TreeAspect = "spatial",
): string {
  const ancestors = collectAncestors(objectId, nodeById, aspect);
  return ancestors.length > 0 ? ancestors[ancestors.length - 1] : objectId;
}

// ─────────────────────────────────────────────────────────────────────────────
// Keep-set builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Keep-set for `isolate(objectId)` (Task A.3): the target plus its ancestor
 * path in the given aspect.
 */
export function ancestorPathKeepSet(
  objectId: string,
  nodeById: ReadonlyMap<string, UniversalSceneNode>,
  aspect: TreeAspect = "spatial",
): Set<string> {
  return new Set<string>([objectId, ...collectAncestors(objectId, nodeById, aspect)]);
}

/**
 * Keep-set for `isolateSystem(objectId)` (Task C): the target plus its entire
 * subtree (descendants) in the given aspect.
 */
export function subtreeKeepSet(
  objectId: string,
  nodes: readonly UniversalSceneNode[],
  aspect: TreeAspect = "spatial",
): Set<string> {
  return new Set<string>([objectId, ...collectDescendants(objectId, nodes, aspect)]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Overlay-delta computation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Turns a "keep visible" set into a full overlay delta over `allObjectIds`:
 *   - kept objects     → `{ visible: true,  isolated: true }`
 *   - all other objects → `{ visible: false, isolated: false }`
 *
 * The result is a `Record<objectId, Partial<ControlOverlayEntry>>` suitable for
 * `mergeControlOverlay`. Pure — does not read or write the store.
 */
export function computeIsolation(
  keepIds: ReadonlySet<string>,
  allObjectIds: readonly string[],
): Record<string, Partial<ControlOverlayEntry>> {
  const delta: Record<string, Partial<ControlOverlayEntry>> = {};
  for (const id of allObjectIds) {
    delta[id] = keepIds.has(id)
      ? { visible: true, isolated: true }
      : { visible: false, isolated: false };
  }
  return delta;
}

/**
 * Reset delta for `clearIsolation()`: every object is restored to
 * `{ visible: true, isolated: false }`. `colorOverride` / `opacity` are left
 * untouched (callers that want a full reset use `clearControlOverlay`).
 */
export function computeClearIsolation(
  allObjectIds: readonly string[],
): Record<string, Partial<ControlOverlayEntry>> {
  const delta: Record<string, Partial<ControlOverlayEntry>> = {};
  for (const id of allObjectIds) {
    delta[id] = { visible: true, isolated: false };
  }
  return delta;
}

// ─────────────────────────────────────────────────────────────────────────────
// Color-by-aspect (Task C)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deterministic palette of design-token aliases for "Color by aspect".
 * Token aliases ONLY — never raw hex (scene-hierarchy types §6). Each
 * top-level aspect group is assigned `palette[groupIndex % palette.length]`.
 */
export const ASPECT_COLOR_PALETTE: readonly string[] = [
  "color.accent.primary",
  "color.semantic.info",
  "color.semantic.warning",
  "color.semantic.success",
  "color.semantic.danger",
  "color.accent.strong",
];

/**
 * Computes a `colorOverride` delta that colours every member of `aspect` by
 * its top-level group. Groups are assigned palette colours deterministically:
 * group ids are sorted ascending and indexed into {@link ASPECT_COLOR_PALETTE},
 * so the same input always yields the same colours regardless of node order.
 *
 * Non-members of `aspect` receive no entry (left untouched).
 */
export function computeColorByAspect(
  nodes: readonly UniversalSceneNode[],
  aspect: TreeAspect = "spatial",
): Record<string, Partial<ControlOverlayEntry>> {
  const nodeById = indexNodesById(nodes);

  // Map each member node → its top-level group id.
  const groupOf = new Map<string, string>();
  const groupIds = new Set<string>();
  for (const node of nodes) {
    if (!isMemberOfAspect(node, aspect)) continue;
    const group = topLevelGroupId(node.objectId, nodeById, aspect);
    groupOf.set(node.objectId, group);
    groupIds.add(group);
  }

  // Deterministic group → palette-index assignment (sort group ids).
  const sortedGroups = [...groupIds].sort((a, b) =>
    a < b ? -1 : a > b ? 1 : 0,
  );
  const colorByGroup = new Map<string, string>();
  sortedGroups.forEach((groupId, index) => {
    colorByGroup.set(
      groupId,
      ASPECT_COLOR_PALETTE[index % ASPECT_COLOR_PALETTE.length],
    );
  });

  const delta: Record<string, Partial<ControlOverlayEntry>> = {};
  for (const [objectId, groupId] of groupOf) {
    const colorOverride = colorByGroup.get(groupId);
    if (colorOverride) delta[objectId] = { colorOverride };
  }
  return delta;
}
